/**
 * Delete Device Type Form Composer
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  DeleteDeviceTypeFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type DeleteDeviceTypeFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderTextControl, renderButtonControl, type ControlConfig } from '../../formRenderFunctions'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type DeleteDeviceTypeComposerProps = {
  deviceTypeNumber: number
  deviceTypeName: string
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof DeleteDeviceTypeFormData, value: unknown): string | null => {
  const shape = DeleteDeviceTypeFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function DeleteDeviceTypeComposer(props: DeleteDeviceTypeComposerProps) {
  const { deviceTypeNumber, deviceTypeName, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const deleteDeviceType = useApiMutation('archiveDeviceType', 'DELETE', {
    invalidates: ['getDeviceTypes'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to delete device type:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: DeleteDeviceTypeFormSchema,
    defaultValues: {
      deviceTypeNumber: deviceTypeNumber,
      deviceTypeName: deviceTypeName,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as DeleteDeviceTypeFormData
      const parsed = DeleteDeviceTypeFormSchema.safeParse({
        deviceTypeNumber: deviceTypeNumber,
        deviceTypeName: formValue.deviceTypeName ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string' && (fieldName === 'deviceTypeNumber' || fieldName === 'deviceTypeName')) {
            form.setFieldMeta(fieldName as keyof DeleteDeviceTypeFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      await deleteDeviceType.mutateAsync(apiRequest)
    },
  })

  const renderControl = (control: any) => {
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof DeleteDeviceTypeFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: false,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? true,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}
        {renderFormContent()}
        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: deleteDeviceType.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Deleting...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Delete'),
            variant: 'destructive',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}

export {
  DeleteDeviceTypeFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type DeleteDeviceTypeFormData,
} from './schema'
